import { useMemo } from 'react';
import { getRouteApi } from '@tanstack/react-router';
import { useUrlSyncedFilters } from '@/hooks/useUrlSyncedFilters';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { DATE_RANGE_FILTER_KEYS } from '@/utils/filterFields';
import type { DcrApprovalParams, DerApprovalParams } from '../types/billing.types';
import { toApprovalFilterSearch, toApprovalFilterValues } from '../utils/billingFilters';
import { getQueueDefaultPeriod } from '../utils/period';

const ROUTES = {
  approval: getRouteApi('/_auth/billing/approval'),
  amendment: getRouteApi('/_auth/billing/cost-amendment'),
};

export type ApprovalRoute = keyof typeof ROUTES;

const normalizeFilterValues = (values: Record<string, string>) =>
  toApprovalFilterValues(toApprovalFilterSearch(values));

export function useApprovalFilters(routeId: ApprovalRoute) {
  const route = ROUTES[routeId];
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const appliedValues = useMemo(() => toApprovalFilterValues(search), [search]);
  const defaultValues = useMemo(() => getQueueDefaultPeriod(), []);

  const filters = useUrlSyncedFilters({
    appliedValues,
    shouldWaitForApply: true,
    defaultValues,
    rangeKeys: DATE_RANGE_FILTER_KEYS,
    normalizeValues: normalizeFilterValues,
    onApply: (values) => {
      void navigate({
        search: (previous) => ({ ...previous, ...toApprovalFilterSearch(values) }),
        replace: true,
      });
    },
  });

  const period = useMemo(
    () => ({
      dateFrom: search.dateFrom ?? defaultValues.dateFrom,
      dateTo: search.dateTo ?? defaultValues.dateTo,
    }),
    [search.dateFrom, search.dateTo, defaultValues],
  );

  const dcrParams = useMemo<DcrApprovalParams>(() => {
    const { expenseTypeId: _expenseTypeId, minAmount: _minAmount, ...rest } = search;
    return { ...rest, ...period };
  }, [search, period]);

  const derParams = useMemo<DerApprovalParams>(() => {
    const { dcrTypeId: _dcrTypeId, minFare: _minFare, ...rest } = search;
    return { ...rest, ...period };
  }, [search, period]);

  const zmSearch = useUrlSyncedSearch({
    appliedText: search.search,
    onApply: (text) => {
      void navigate({
        search: (previous) => ({ ...previous, search: text }),
        replace: true,
      });
    },
  });

  return { ...filters, ...zmSearch, period, dcrParams, derParams };
}
